import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import SocialShare from '../components/SocialShare';
import FormGuide from '../components/FormGuide';
import PeakPerformance from '../components/PeakPerformance';
import StatsDashboard from '../components/StatsDashboard';
import MonthlyComparison from '../components/MonthlyComparison';
import TournamentPerformance from '../components/TournamentPerformance';
import PlayerEndorsements from '../components/PlayerEndorsements';
import PlayerProfile from '../components/PlayerProfile';
import PointsHistoryChart from '../components/PointsHistoryChart';
import HeadToHeadCard from '../components/HeadToHeadCard';
import ChampionTitles from '../components/ChampionTitles';
import AchievementBadges from '../components/AchievementBadges';
import AdvancedStats from '../components/AdvancedStats';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trophy, Medal, Users, UserPlus, UserMinus, Edit, MapPin, Calendar, Hand, Activity, Heart, Target, CheckCircle, XCircle, ZoomIn, MessageCircle, Share2 } from 'lucide-react';
import { ShareCardDialog } from '../components/ShareCardDialog';
import { toast } from 'sonner';

// Form Guide Card Component
const FormGuideCard = ({ userId }) => {
  const { API } = useContext(AuthContext);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFormGuide();
  }, [userId]);

  const fetchFormGuide = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/form-guide`);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to fetch form guide:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Activity className="w-5 h-5" />
            מצב נוכחי
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">טוען...</div>
        </CardContent>
      </Card>
    );
  }

  if (!formData || formData.form.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Activity className="w-5 h-5" />
            מצב נוכחי
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-4">
            אין משחקים אחרונים להצגה
          </div>
        </CardContent>
      </Card>
    );
  }

  const getFormColor = (percentage) => {
    if (percentage >= 80) return 'from-green-500 to-green-600';
    if (percentage >= 60) return 'from-blue-500 to-blue-600';
    if (percentage >= 40) return 'from-yellow-500 to-yellow-600';
    return 'from-red-500 to-red-600';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <Activity className="w-5 h-5" />
          מצב נוכחי - 5 משחקים אחרונים
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* תצוגת W-L-W-W-L */}
          <div className="flex items-center justify-center gap-2">
            <FormGuide form={formData.form} size="normal" />
          </div>

          {/* אחוז ניצחונות */}
          <div className={`p-6 rounded-lg bg-gradient-to-r ${getFormColor(formData.form_percentage)} text-white text-center`}>
            <div className="text-4xl font-bold mb-1">
              {formData.form_percentage}%
            </div>
            <div className="text-sm opacity-90">
              {formData.wins_in_last_5} ניצחונות מתוך 5
            </div>
          </div>

          {/* הסבר */}
          <p className="text-sm text-gray-600 text-center">
            {formData.form_percentage >= 80
              ? '🔥 במצב מצוין! ממשיך ככה!'
              : formData.form_percentage >= 60
              ? '💪 במצב טוב, תמשיך לעבוד קשה'
              : formData.form_percentage >= 40
              ? '⚡ יש מקום לשיפור, תתאמן יותר'
              : '💙 תקופה קשה, אל תתייאש!'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Match History Component
const MatchHistory = ({ userId }) => {
  const { API } = useContext(AuthContext);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [userId]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch match history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Target className="w-6 h-6" />
            היסטוריית משחקים
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">טוען...</div>
        </CardContent>
      </Card>
    );
  }

  if (!history || history.matches.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Target className="w-6 h-6" />
            היסטוריית משחקים
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-8">
            אין משחקים להצגה
          </div>
        </CardContent>
      </Card>
    );
  }

  const getRoundLabel = (round) => {
    const labels = {
      'final': 'גמר',
      'semi_final': 'חצי גמר',
      'quarter_final': 'רבע גמר',
      'round_of_16': 'שמינית גמר',
      'round_of_32': '1/32',
      'group_stage': 'שלב בתים',
      'group_a': 'בית א',
      'group_b': 'בית ב',
      'group_c': 'בית ג',
      'group_d': 'בית ד',
      'group_e': 'בית ה',
      'group_f': 'בית ו',
      'consolation_final': 'גמר תנחומים',
      'third_place': 'מקום שלישי',
    };
    return labels[round] || round;
  };

  const formatHebrewDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <Target className="w-6 h-6" />
          היסטוריית משחקים ({history.total_matches})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div 
          className="space-y-4 overflow-y-auto pr-2 custom-scrollbar"
          style={{ maxHeight: '600px' }}
          data-testid="match-history-scroll"
        >
          {history.matches.map((match) => (
            <div 
              key={match.id}
              className={`p-4 rounded-lg border-2 ${
                match.is_winner 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {match.is_winner ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-bold ${match.is_winner ? 'text-green-900' : 'text-red-900'}`}>
                    {match.is_winner ? 'ניצחון' : 'הפסד'}
                  </span>
                  <span className="text-gray-600">•</span>
                  <span className="text-gray-700">{getRoundLabel(match.round)}</span>
                  {match.tournament_type === 'daily' && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-300">
                      תחרות יומית
                    </span>
                  )}
                  {match.tournament_type === 'knockout' && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-300">
                      תחרות נוקאאוט ארוכה
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {formatHebrewDate(match.tournament_date)}
                </span>
              </div>

              <div className="flex items-center gap-3 mb-2">
                <Avatar className="w-8 h-8">
                  {match.opponent_avatar && <AvatarImage src={match.opponent_avatar} />}
                  <AvatarFallback className="bg-gray-600 text-white text-sm">
                    {match.opponent.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">מול {match.opponent}</p>
                  <p className="text-sm text-gray-600">{match.tournament_name}</p>
                </div>
              </div>

              {match.sets && match.sets.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {match.sets.map((set, idx) => (
                    <div key={idx} className="px-3 py-1 bg-white rounded border text-sm font-mono">
                      {set.player1_games}-{set.player2_games}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {history.total_matches > 10 && (
          <div className="text-center text-xs text-gray-500 pt-3 border-t mt-2">
            סה"כ {history.total_matches} משחקים · גלול לראות עוד
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ProfilePage = () => {
  const { userId } = useParams();
  const { API, user } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [computedStats, setComputedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Image zoom modal state
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);
  
  // Points adjustment modal state (admin only)
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isShareCardOpen, setIsShareCardOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', content: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [pointsAdjustment, setPointsAdjustment] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  
  // Stats edit modal state (admin only)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [statsForm, setStatsForm] = useState({
    total_points: 0,
    total_wins: 0,
    total_losses: 0,
    total_tournaments: 0
  });
  const [isSavingStats, setIsSavingStats] = useState(false);
  
  // Edit profile modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    skill_level: '',
    city: '',
    birth_year: '',
    playing_hand: '',
    years_playing: '',
    favorite_surface: '',
    bio: '',
    playing_style: '',
    strengths: '',
    weaknesses: '',
    favorite_player_1: '',
    racket: '',
    shoes: '',
    goals: '',
    avatar: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setProfile(response.data);
      axios.get(`${API}/users/${userId}/stats-dashboard`)
        .then((r) => setComputedStats(r.data))
        .catch(() => {});
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('שגיאה בטעינת פרופיל');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error('עליך להיות מחובר');
      return;
    }

    try {
      const response = await axios.post(`${API}/follow`, { target_user_id: userId });
      toast.success(response.data.message);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה');
    }
  };

  const handleUnfollow = async () => {
    try {
      const response = await axios.post(`${API}/unfollow`, { target_user_id: userId });
      toast.success(response.data.message);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה');
    }
  };
  
  const openEditModal = () => {
    setEditForm({
      full_name: profile.full_name || '',
      skill_level: profile.skill_level || '',
      city: profile.city || '',
      birth_year: profile.birth_year || '',
      playing_hand: profile.playing_hand || '',
      years_playing: profile.years_playing || '',
      favorite_surface: profile.favorite_surface || '',
      bio: profile.bio || '',
      playing_style: profile.playing_style || '',
      strengths: profile.strengths || '',
      weaknesses: profile.weaknesses || '',
      favorite_player_1: profile.favorite_player_1 || '',
      racket: profile.racket || '',
      shoes: profile.shoes || '',
      goals: profile.goals || '',
      avatar: profile.avatar || '',
      whatsapp: profile.whatsapp || '',
      facebook: profile.facebook || '',
      instagram: profile.instagram || '',
      youtube: profile.youtube || ''
    });
    setAvatarPreview(profile.avatar || null);
    setAvatarFile(null);
    setIsEditModalOpen(true);
  };
  
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // בדיקת סוג הקובץ
      if (!file.type.startsWith('image/')) {
        toast.error('יש לבחור קובץ תמונה בלבד');
        return;
      }
      
      // בדיקת גודל (מקסימום 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('הקובץ גדול מדי. מקסימום 5MB');
        return;
      }
      
      setAvatarFile(file);
      
      // יצירת preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // הכנת הנתונים לעדכון
      let updatedForm = { ...editForm };
      
      // אם יש קובץ תמונה חדש, להמיר אותו ל-base64
      if (avatarFile) {
        try {
          const reader = new FileReader();
          
          const base64String = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(avatarFile);
          });
          
          updatedForm.avatar = base64String;
        } catch (readerError) {
          console.error('Failed to read image:', readerError);
          toast.error('שגיאה בקריאת התמונה');
          setIsSaving(false);
          return;
        }
      }
      
      // הסרת שדות ריקים
      Object.keys(updatedForm).forEach(key => {
        if (updatedForm[key] === '' || updatedForm[key] === null) {
          delete updatedForm[key];
        }
      });
      
      // בדיקה אם זה הפרופיל של המשתמש הנוכחי או אדמין עורך פרופיל אחר
      let endpoint = `${API}/users/profile`;
      if (user?.is_admin && !isOwnProfile) {
        // אדמין עורך פרופיל של משתמש אחר
        endpoint = `${API}/admin/users/${userId}/profile`;
      }
      
      // עדכון הפרופיל
      const response = await axios.put(
        endpoint,
        updatedForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('הפרופיל עודכן בהצלחה! 🎉');
      setIsEditModalOpen(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      fetchProfile(); // רענון הפרופיל
    } catch (error) {
      console.error('Failed to update profile:', error);
      const errorMsg = error.response?.data?.detail || 'שגיאה בעדכון פרופיל';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleAdjustPoints = async () => {
    const adjustmentValue = parseInt(pointsAdjustment);
    
    if (isNaN(adjustmentValue) || adjustmentValue === 0) {
      toast.error('יש להזין מספר שונה מ-0');
      return;
    }
    
    setIsAdjusting(true);
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/users/${userId}/adjust-points`,
        {
          points_adjustment: adjustmentValue,
          reason: adjustmentReason || 'עדכון ידני על ידי אדמין'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      toast.success(`הנקודות עודכנו בהצלחה! ${adjustmentValue > 0 ? '+' : ''}${adjustmentValue} נקודות`);
      setIsPointsModalOpen(false);
      setPointsAdjustment('');
      setAdjustmentReason('');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בעדכון נקודות');
    } finally {
      setIsAdjusting(false);
    }
  };

  // פתיחת modal לעריכת סטטיסטיקות
  const openStatsModal = () => {
    setStatsForm({
      total_points: profile?.total_points || 0,
      total_wins: profile?.total_wins || 0,
      total_losses: profile?.total_losses || 0,
      total_tournaments: profile?.total_tournaments || 0
    });
    setIsStatsModalOpen(true);
  };

  // שמירת סטטיסטיקות
  const handleSaveStats = async () => {
    setIsSavingStats(true);
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${userId}/stats`,
        {
          total_points: parseInt(statsForm.total_points) || 0,
          total_wins: parseInt(statsForm.total_wins) || 0,
          total_losses: parseInt(statsForm.total_losses) || 0,
          total_tournaments: parseInt(statsForm.total_tournaments) || 0
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      toast.success('הסטטיסטיקות עודכנו בהצלחה!');
      setIsStatsModalOpen(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בעדכון סטטיסטיקות');
    } finally {
      setIsSavingStats(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <div className="animate-pulse text-xl text-gray-600">טוען...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-xl text-gray-600">השחקן לא נמצא</p>
        </div>
      </div>
    );
  }

  const isOwnProfile = user && user.id === userId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Profile Header */}
        <Card className="mb-8 glass-effect" data-testid="profile-header-card">
          <CardContent className="pt-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="relative group">
                <Avatar 
                  className="w-32 h-32 cursor-pointer transition-transform hover:scale-105"
                  onClick={() => profile.avatar && setIsImageZoomOpen(true)}
                >
                  {profile.avatar && <AvatarImage src={profile.avatar} alt={profile.full_name} />}
                  <AvatarFallback className="text-4xl bg-gradient-to-r from-blue-600 to-green-600 text-white">
                    {profile.full_name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {profile.avatar && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all pointer-events-none">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </div>

              <div className="flex-1 text-center md:text-right">
                <div className="flex items-center justify-center md:justify-end gap-3 mb-2">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900" data-testid="profile-name">
                    {profile.full_name}
                  </h1>
                  {(isOwnProfile || (user && user.is_admin)) && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      data-testid="edit-profile-btn"
                      onClick={openEditModal}
                      className={user?.is_admin && !isOwnProfile ? "border-purple-500 text-purple-600 hover:bg-purple-50" : ""}
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      {user?.is_admin && !isOwnProfile ? "ערוך פרופיל (אדמין)" : "ערוך פרופיל"}
                    </Button>
                  )}
                  {/* Send message button - visible when viewing OTHER user's profile */}
                  {user && !isOwnProfile && (
                    <Button
                      size="sm"
                      data-testid="send-message-btn"
                      onClick={() => setIsMessageDialogOpen(true)}
                      className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                    >
                      <MessageCircle className="w-4 h-4 ml-1" />
                      שלח הודעה
                    </Button>
                  )}
                </div>
                <p className="text-lg md:text-xl text-blue-600 font-medium mb-4">{profile.skill_level}</p>
                
                {/* נקודות ודירוג */}
                <div className="flex flex-wrap justify-center md:justify-end gap-4 mb-4">
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 rounded-lg shadow-lg">
                    <div className="text-2xl font-bold">{profile.total_points || 0}</div>
                    <div className="text-xs opacity-90">נקודות</div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-lg shadow-lg">
                    <div className="text-2xl font-bold">#{profile.rank || '---'}</div>
                    <div className="text-xs opacity-90">דירוג</div>
                  </div>
                  {isOwnProfile && (
                    <Button
                      size="sm"
                      onClick={() => setIsShareCardOpen(true)}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg self-center"
                      data-testid="open-share-card-btn"
                    >
                      <Share2 className="w-4 h-4 ml-1" />
                      שתף כרטיס שחקן
                    </Button>
                  )}
                  {/* כפתור עדכון נקודות ידני - רק לאדמין */}
                  {user && user.is_admin && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-purple-500 text-purple-600 hover:bg-purple-50"
                      onClick={() => setIsPointsModalOpen(true)}
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      עדכן נקודות
                    </Button>
                  )}
                </div>
                
                {/* Additional Info */}
                <div className="flex flex-wrap justify-center md:justify-end gap-4 text-sm md:text-base text-gray-600 mb-4">
                  {profile.city && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{profile.city}</span>
                    </div>
                  )}
                  {profile.birth_year && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>גיל {new Date().getFullYear() - profile.birth_year}</span>
                    </div>
                  )}
                  {profile.playing_hand && (
                    <div className="flex items-center gap-1">
                      <Hand className="w-4 h-4" />
                      <span>{profile.playing_hand}</span>
                    </div>
                  )}
                  {profile.years_playing && (
                    <div className="flex items-center gap-1">
                      <Activity className="w-4 h-4" />
                      <span>{profile.years_playing} שנות משחק</span>
                    </div>
                  )}
                  {/* מספר טלפון - רק לאדמין - קישור ישיר לוואטסאפ */}
                  {user && user.is_admin && profile.phone && (() => {
                    const cleanPhone = profile.phone.replace(/[-\s()]/g, '');
                    const intlPhone = cleanPhone.startsWith('0') ? '972' + cleanPhone.slice(1) : cleanPhone.startsWith('+') ? cleanPhone.slice(1) : cleanPhone;
                    const firstName = (profile.full_name || '').split(' ')[0] || 'שלום';
                    const text = encodeURIComponent(`היי ${firstName}, מדבר/ת מ-Tennis Pro Israel 🎾`);
                    const waUrl = `https://wa.me/${intlPhone}?text=${text}`;
                    return (
                      <a
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="admin-whatsapp-link"
                        className="inline-flex items-center gap-1 bg-green-50 hover:bg-green-100 px-2 py-1 rounded border border-green-300 transition-colors"
                        title="לחץ לפתיחת WhatsApp"
                      >
                        <span className="text-xs font-bold text-green-700">📱 WhatsApp (אדמין):</span>
                        <span className="font-medium text-green-800 underline-offset-2 hover:underline" dir="ltr">{profile.phone}</span>
                      </a>
                    );
                  })()}
                  {profile.favorite_surface && (
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      <span>{profile.favorite_surface}</span>
                    </div>
                  )}
                </div>

                {profile.bio && (
                  <div className="mb-4 max-w-2xl mx-auto md:mx-0">
                    <div className="flex items-start gap-2 justify-end">
                      <p className="text-gray-700 leading-relaxed flex-1">
                        {profile.bio}
                      </p>
                      {user && user.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="edit-bio-btn"
                          onClick={openEditModal}
                          className="h-7 px-2 border-purple-400 text-purple-600 hover:bg-purple-50 shrink-0"
                        >
                          <Edit className="w-3.5 h-3.5 ml-1" />
                          <span className="text-xs">ערוך תיאור</span>
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Social Media Links */}
                {(profile.whatsapp || profile.facebook || profile.instagram || profile.youtube || (user && user.is_admin)) && (
                  <div className="mb-6" data-testid="social-links">
                    <div className="flex flex-wrap justify-center md:justify-end gap-3 items-center">
                      {profile.whatsapp && (
                      <a
                        href={`https://wa.me/${profile.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-link"
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium shadow-lg shadow-green-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </a>
                    )}
                    {profile.facebook && (
                      <a
                        href={profile.facebook.startsWith('http') ? profile.facebook : `https://facebook.com/${profile.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="facebook-link"
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        Facebook
                      </a>
                    )}
                    {profile.instagram && (
                      <a
                        href={profile.instagram.startsWith('http') ? profile.instagram : `https://instagram.com/${profile.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-link"
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 via-purple-500 to-orange-400 text-white rounded-xl font-medium shadow-lg shadow-purple-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        Instagram
                      </a>
                    )}
                    {profile.youtube && (
                      <a
                        href={profile.youtube.startsWith('http') ? profile.youtube : `https://youtube.com/${profile.youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="youtube-link"
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl font-medium shadow-lg shadow-red-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                        YouTube
                      </a>
                    )}
                    {user && user.is_admin && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="edit-social-btn"
                        onClick={openEditModal}
                        className="h-9 px-3 border-purple-400 text-purple-600 hover:bg-purple-50"
                      >
                        <Edit className="w-3.5 h-3.5 ml-1" />
                        <span className="text-xs">ערוך רשתות חברתיות</span>
                      </Button>
                    )}
                    </div>
                  </div>
                )}

                {/* Goals Section */}
                {(profile.goals || (user && user.is_admin)) && (
                  <div className="mb-6 bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border-2 border-yellow-300">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🎯</span>
                        <span className="font-bold text-yellow-900">מטרות לעונה:</span>
                      </div>
                      {user && user.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="edit-goals-btn"
                          onClick={openEditModal}
                          className="h-7 px-2 border-purple-400 text-purple-600 hover:bg-purple-50"
                        >
                          <Edit className="w-3.5 h-3.5 ml-1" />
                          <span className="text-xs">ערוך</span>
                        </Button>
                      )}
                    </div>
                    <p className="text-yellow-800 font-medium">{profile.goals || <span className="text-gray-400 italic">לא הוזנו מטרות</span>}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-gray-700 mb-6">
                  <div className="flex flex-col items-center md:items-end gap-1 p-3 bg-yellow-50 rounded-lg">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                    <div className="font-bold text-xl" data-testid="profile-header-wins">{computedStats ? computedStats.total_wins : (profile.total_wins || 0)}</div>
                    <div className="text-sm">ניצחונות</div>
                  </div>
                  <div className="flex flex-col items-center md:items-end gap-1 p-3 bg-blue-50 rounded-lg">
                    <Medal className="w-6 h-6 text-blue-600" />
                    <div className="font-bold text-xl" data-testid="profile-header-points">{profile.total_points || 0}</div>
                    <div className="text-sm">נקודות</div>
                  </div>
                  <div className="flex flex-col items-center md:items-end gap-1 p-3 bg-green-50 rounded-lg">
                    <Users className="w-6 h-6 text-green-600" />
                    <div className="font-bold text-xl">{profile.total_tournaments || 0}</div>
                    <div className="text-sm">תחרויות</div>
                  </div>
                  <div className="flex flex-col items-center md:items-end gap-1 p-3 bg-red-50 rounded-lg">
                    <Trophy className="w-6 h-6 text-red-600" />
                    <div className="font-bold text-xl" data-testid="profile-header-losses">{computedStats ? computedStats.total_losses : (profile.total_losses || 0)}</div>
                    <div className="text-sm">הפסדים</div>
                  </div>
                </div>
                
                {/* כפתור עריכת סטטיסטיקות - רק לאדמין */}
                {user && user.is_admin && (
                  <div className="mb-6">
                    <Button 
                      variant="outline"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      onClick={openStatsModal}
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      ערוך סטטיסטיקות שחקן
                    </Button>
                  </div>
                )}

                {/* Player Profile (Playing Style, Favorite Players, Equipment) */}
                <PlayerProfile 
                  profile={profile} 
                  className="mb-6"
                  onAdminEdit={user && user.is_admin ? openEditModal : null}
                />

                {/* Best Results */}
                {(profile.best_result_daily || profile.best_result_knockout) && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg">
                    <h3 className="font-bold text-lg mb-2">תוצאות מצטיינות</h3>
                    <div className="flex flex-wrap gap-4 text-sm">
                      {profile.best_result_daily && (
                        <div>
                          <span className="text-gray-600">תחרויות יומיות:</span>
                          <span className="font-bold text-yellow-700 mr-2">{profile.best_result_daily}</span>
                        </div>
                      )}
                      {profile.best_result_knockout && (
                        <div>
                          <span className="text-gray-600">נוקאאוט:</span>
                          <span className="font-bold text-yellow-700 mr-2">{profile.best_result_knockout}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-center md:justify-end gap-4 mt-6">
                  <div className="text-center">
                    <div className="font-bold text-2xl">{profile.followers_count || 0}</div>
                    <div className="text-gray-600 text-sm">עוקבים</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-2xl">{profile.following_count || 0}</div>
                    <div className="text-gray-600 text-sm">עוקב</div>
                  </div>
                </div>

                {!isOwnProfile && user && (
                  <div className="mt-6 flex flex-col gap-3">
                    <div className="flex gap-3 flex-wrap justify-center md:justify-end">
                      {profile.is_following ? (
                        <Button 
                          variant="outline" 
                          onClick={handleUnfollow}
                          className="flex items-center gap-2"
                          data-testid="unfollow-btn"
                        >
                          <UserMinus className="w-4 h-4" />
                          בטל מעקב
                        </Button>
                      ) : (
                        <Button 
                          onClick={handleFollow}
                          className="btn-primary flex items-center gap-2"
                          data-testid="follow-btn"
                        >
                          <UserPlus className="w-4 h-4" />
                          עקוב
                        </Button>
                      )}
                      
                      {/* כפתור מחיקה למנהל בלבד */}
                      {user && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email) && profile.id !== user.id && !profile.is_admin && (
                        <Button
                          onClick={async () => {
                            if (window.confirm(`⚠️ האם למחוק את ${profile.full_name}?\n\nפעולה זו בלתי הפיכה!`)) {
                              try {
                                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                await axios.delete(`${API}/admin/users/${profile.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('המשתמש נמחק');
                                window.location.href = '/rankings';
                              } catch (error) {
                                toast.error('שגיאה במחיקה');
                              }
                            }
                          }}
                          variant="destructive"
                          className="flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          מחק משתמש
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Champion Titles */}
        <ChampionTitles userId={userId} />

        {/* Achievement Badges + Win Streak */}
        <AchievementBadges userId={userId} />

        {/* Stats Dashboard */}
        <StatsDashboard userId={userId} />

        {/* Advanced Stats: Rivalry, Personal Best, Set stats, Heatmap */}
        <AdvancedStats userId={userId} />


        {/* Points History Chart */}
        <PointsHistoryChart userId={userId} />

        {/* Form Guide & Peak Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PeakPerformance userId={userId} />
          <FormGuideCard userId={userId} />
        </div>

        {/* Monthly Comparison & Tournament Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MonthlyComparison userId={userId} />
          <TournamentPerformance userId={userId} />
        </div>

        {/* Player Endorsements */}
        <PlayerEndorsements userId={userId} />

        {/* Head-to-Head vs current user */}
        {!isOwnProfile && user && (
          <HeadToHeadCard
            meId={user.id}
            meName={user.full_name || 'אתה'}
            opponentId={userId}
            opponentName={profile.full_name || 'היריב'}
          />
        )}

        {/* Match History */}
        <MatchHistory userId={userId} />
        
        {/* Tournament History */}
        <Card data-testid="tournament-history-card">
          <CardHeader>
            <CardTitle className="text-2xl">היסטוריית תחרויות</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.tournament_history && profile.tournament_history.length > 0 ? (
              <div className="space-y-4">
                {profile.tournament_history.map((tournament) => (
                  <div 
                    key={tournament.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`tournament-history-${tournament.id}`}
                  >
                    <div>
                      <h3 className="font-bold text-lg">{tournament.name}</h3>
                      <p className="text-gray-600">
                        {tournament.category} • {new Date(tournament.date).toLocaleDateString('he-IL')}
                      </p>
                    </div>
                    <div className="text-left">
                      <span className={
                        tournament.status === 'open' ? 'status-open' :
                        tournament.status === 'full' ? 'status-full' :
                        tournament.status === 'completed' ? 'bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold border border-blue-300' :
                        'status-in-progress'
                      }>
                        {tournament.status === 'open' ? 'פתוח' :
                         tournament.status === 'full' ? 'מלא' :
                         tournament.status === 'completed' ? 'הסתיים' :
                         'בעיצומו'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-600 py-8">אין היסטוריית תחרויות עדיין</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Image Zoom Modal */}
      <Dialog open={isImageZoomOpen} onOpenChange={setIsImageZoomOpen}>
        <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw]">
          <DialogHeader>
            <DialogTitle>{profile?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            <img
              src={profile?.avatar}
              alt={profile?.full_name}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
      

      {/* Send Message Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-pink-600" />
              שלח הודעה ל-{profile?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 p-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">נושא</label>
              <input
                type="text"
                value={messageForm.subject}
                onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                placeholder="לדוגמה: משחק ביום שלישי"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                data-testid="message-subject-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">תוכן ההודעה</label>
              <textarea
                value={messageForm.content}
                onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                placeholder="כתוב את ההודעה שלך..."
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                data-testid="message-content-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsMessageDialogOpen(false)} disabled={sendingMessage}>ביטול</Button>
              <Button
                disabled={sendingMessage}
                className="bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-700 hover:to-rose-600 text-white"
                data-testid="send-message-submit"
                onClick={async () => {
                  if (!messageForm.subject.trim() || !messageForm.content.trim()) {
                    toast.error('נושא ותוכן נדרשים');
                    return;
                  }
                  setSendingMessage(true);
                  try {
                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                    await axios.post(
                      `${API}/messages/send`,
                      {
                        recipient_id: profile?.id,
                        subject: messageForm.subject,
                        content: messageForm.content,
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('ההודעה נשלחה!');
                    setIsMessageDialogOpen(false);
                    setMessageForm({ subject: '', content: '' });
                  } catch (e) {
                    toast.error('שגיאה בשליחת הודעה');
                  } finally {
                    setSendingMessage(false);
                  }
                }}
              >
                <MessageCircle className="w-4 h-4 ml-2" />
                שלח
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Points Adjustment Modal (Admin Only) */}
      <Dialog open={isPointsModalOpen} onOpenChange={setIsPointsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-purple-700">עדכון נקודות ידני</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <strong>נקודות נוכחיות:</strong> {profile?.total_points || 0}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                כמות נקודות להוסיף / להוריד
              </label>
              <input
                type="number"
                value={pointsAdjustment}
                onChange={(e) => setPointsAdjustment(e.target.value)}
                placeholder="לדוגמה: 50 או -50"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="points-adjustment-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                מספר חיובי להוספה, מספר שלילי להפחתה
              </p>
            </div>
            
            {pointsAdjustment && !isNaN(parseInt(pointsAdjustment)) && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-900">
                  <strong>תוצאה:</strong> {profile?.total_points || 0} {parseInt(pointsAdjustment) > 0 ? '+' : ''}{pointsAdjustment} = {Math.max(0, (profile?.total_points || 0) + parseInt(pointsAdjustment))} נקודות
                </p>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleAdjustPoints}
                disabled={isAdjusting}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isAdjusting ? 'מעדכן...' : 'עדכן נקודות'}
              </Button>
              <Button
                onClick={() => {
                  setIsPointsModalOpen(false);
                  setPointsAdjustment('');
                  setAdjustmentReason('');
                }}
                variant="outline"
                disabled={isAdjusting}
              >
                ביטול
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Edit Modal (Admin Only) */}
      <Dialog open={isStatsModalOpen} onOpenChange={setIsStatsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-700">
              📊 עריכת סטטיסטיקות - {profile?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 mb-2">
                ⚠️ שים לב: שינויים אלה ישפיעו ישירות על הסטטיסטיקות של השחקן
              </p>
            </div>
            
            {/* נקודות */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🏆 נקודות
              </label>
              <input
                type="number"
                min="0"
                value={statsForm.total_points}
                onChange={(e) => setStatsForm({...statsForm, total_points: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-lg"
              />
            </div>
            
            {/* ניצחונות */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ✅ ניצחונות
              </label>
              <input
                type="number"
                min="0"
                value={statsForm.total_wins}
                onChange={(e) => setStatsForm({...statsForm, total_wins: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-lg"
              />
            </div>
            
            {/* הפסדים */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ❌ הפסדים
              </label>
              <input
                type="number"
                min="0"
                value={statsForm.total_losses}
                onChange={(e) => setStatsForm({...statsForm, total_losses: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-lg"
              />
            </div>
            
            {/* מספר תחרויות */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🎾 מספר תחרויות
              </label>
              <input
                type="number"
                min="0"
                value={statsForm.total_tournaments}
                onChange={(e) => setStatsForm({...statsForm, total_tournaments: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-lg"
              />
            </div>
            
            {/* כפתורים */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveStats}
                disabled={isSavingStats}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {isSavingStats ? 'שומר...' : '💾 שמור שינויים'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsStatsModalOpen(false)}
                disabled={isSavingStats}
              >
                ביטול
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-600">
              ✏️ עריכת פרופיל
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Avatar Upload */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-blue-200">
              <label className="block text-lg font-bold text-blue-700 mb-4">
                📸 תמונת פרופיל
              </label>
              
              <div className="flex flex-col items-center gap-4">
                {/* תצוגה מקדימה */}
                <div className="relative">
                  <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                    {avatarPreview ? (
                      <AvatarImage src={avatarPreview} alt="Preview" />
                    ) : (
                      <AvatarFallback className="text-4xl bg-gradient-to-r from-blue-600 to-green-600 text-white">
                        {profile?.full_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </div>
                
                {/* כפתור העלאה */}
                <div className="flex flex-col items-center gap-2">
                  <input
                    type="file"
                    id="avatar-upload"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="cursor-pointer px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    📤 בחר תמונה חדשה
                  </label>
                  <p className="text-xs text-gray-500 text-center">
                    JPG, PNG או GIF - מקסימום 5MB
                  </p>
                  {avatarFile && (
                    <p className="text-sm text-green-600 font-medium">
                      ✅ תמונה נבחרה: {avatarFile.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t-2 border-gray-200 my-4"></div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                👤 שם מלא
              </label>
              <input
                type="text"
                value={editForm.full_name || ''}
                onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                placeholder="הזן שם מלא"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Skill Level */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🏆 רמת משחק / קטגוריה
              </label>
              <select
                value={editForm.skill_level}
                onChange={(e) => setEditForm({...editForm, skill_level: e.target.value})}
                className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50"
              >
                <option value="">בחר קטגוריה</option>
                <option value="יחידים open">יחידים open</option>
                <option value="דרג א">דרג א</option>
                <option value="דרג ב">דרג ב</option>
                <option value="דרג ג">דרג ג</option>
                <option value="נשים">נשים</option>
              </select>
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline ml-1" />
                עיר מגורים
              </label>
              <input
                type="text"
                value={editForm.city}
                onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                placeholder="לדוגמה: תל אביב"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Birth Year */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline ml-1" />
                שנת לידה
              </label>
              <input
                type="number"
                value={editForm.birth_year}
                onChange={(e) => setEditForm({...editForm, birth_year: e.target.value})}
                placeholder="לדוגמה: 1990"
                min="1940"
                max={new Date().getFullYear()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Playing Hand */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Hand className="w-4 h-4 inline ml-1" />
                יד משחק
              </label>
              <select
                value={editForm.playing_hand}
                onChange={(e) => setEditForm({...editForm, playing_hand: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">בחר יד</option>
                <option value="ימין">ימין</option>
                <option value="שמאל">שמאל</option>
                <option value="שתי ידיים">שתי ידיים</option>
              </select>
            </div>

            {/* Years Playing */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Activity className="w-4 h-4 inline ml-1" />
                שנות משחק
              </label>
              <input
                type="number"
                value={editForm.years_playing}
                onChange={(e) => setEditForm({...editForm, years_playing: e.target.value})}
                placeholder="כמה שנים את/ה משחק/ת?"
                min="0"
                max="80"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Favorite Surface */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Heart className="w-4 h-4 inline ml-1" />
                משטח אהוב
              </label>
              <select
                value={editForm.favorite_surface}
                onChange={(e) => setEditForm({...editForm, favorite_surface: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">בחר משטח</option>
                <option value="קשה">קשה (Hard)</option>
                <option value="חימר">חימר (Clay)</option>
                <option value="דשא">דשא (Grass)</option>
                <option value="שטיח">שטיח (Carpet)</option>
              </select>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Target className="w-4 h-4 inline ml-1" />
                אודות / ביוגרפיה
              </label>
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                placeholder="ספר/י קצת על עצמך, סגנון המשחק שלך, מטרות..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
              />
            </div>

            {/* Divider */}
            <div className="border-t-2 border-blue-200 pt-4 mt-2">
              <h3 className="text-lg font-bold text-blue-700 mb-3">📊 מאפייני משחק</h3>
            </div>

            {/* Playing Style */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎯 סגנון משחק
              </label>
              <select
                value={editForm.playing_style}
                onChange={(e) => setEditForm({...editForm, playing_style: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">בחר סגנון</option>
                <option value="Aggressive Baseliner">אגרסיבי מאחור (Aggressive Baseliner)</option>
                <option value="Defensive Baseliner">הגנתי מאחור (Defensive Baseliner)</option>
                <option value="Serve and Volley">הגשה ועליה (Serve & Volley)</option>
                <option value="All-Court Player">שחקן כל המגרש (All-Court)</option>
                <option value="Counter Puncher">קאנטר פאנצ'ר (Counter Puncher)</option>
                <option value="Big Hitter">מכות חזקות (Big Hitter)</option>
                <option value="Consistent Player">שחקן עקבי (Consistent)</option>
              </select>
            </div>

            {/* Strengths */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💪 חוזקות
              </label>
              <input
                type="text"
                value={editForm.strengths}
                onChange={(e) => setEditForm({...editForm, strengths: e.target.value})}
                placeholder="לדוגמה: פורהנד, הגשה, חוזק פיזי, משחק רשת"
                className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-green-50"
              />
            </div>

            {/* Weaknesses */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎓 מה לשפר
              </label>
              <input
                type="text"
                value={editForm.weaknesses}
                onChange={(e) => setEditForm({...editForm, weaknesses: e.target.value})}
                placeholder="לדוגמה: בקהנד, סמאש, תנועה לרשת"
                className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-orange-50"
              />
            </div>

            {/* Favorite Player */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ⭐ שחקן אהוב
              </label>
              <input
                type="text"
                value={editForm.favorite_player_1}
                onChange={(e) => setEditForm({...editForm, favorite_player_1: e.target.value})}
                placeholder="לדוגמה: רפאל נדאל, רוג'ר פדרר, נובאק ג'וקוביץ'"
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50"
              />
            </div>

            {/* Divider */}
            <div className="border-t-2 border-blue-200 pt-4 mt-2">
              <h3 className="text-lg font-bold text-blue-700 mb-3">🎾 ציוד ומטרות</h3>
            </div>

            {/* Racket */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎾 מחבט
              </label>
              <input
                type="text"
                value={editForm.racket}
                onChange={(e) => setEditForm({...editForm, racket: e.target.value})}
                placeholder="לדוגמה: Wilson Pro Staff, Babolat Pure Drive, Head Radical"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Shoes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                👟 נעלי משחק
              </label>
              <input
                type="text"
                value={editForm.shoes}
                onChange={(e) => setEditForm({...editForm, shoes: e.target.value})}
                placeholder="לדוגמה: Nike Air Zoom Vapor, Adidas Barricade, Asics Gel Resolution"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Goals */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎯 מטרות לעונה
              </label>
              <textarea
                value={editForm.goals}
                onChange={(e) => setEditForm({...editForm, goals: e.target.value})}
                placeholder="לדוגמה: להגיע לטופ 10, לזכות בטורניר, לשפר את הבקהנד, לשחק יותר תחרותיות..."
                className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-yellow-50"
                rows="3"
              />
            </div>

            {/* Social Media Section */}
            <div className="border-t-2 border-blue-200 pt-4 mt-2">
              <h3 className="text-lg font-bold text-blue-700 mb-3">📱 רשתות חברתיות</h3>
              <p className="text-sm text-gray-500 mb-4">הוסף קישורים כדי שאנשים יוכלו ליצור איתך קשר</p>
            </div>

            {/* WhatsApp */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </span>
              </label>
              <input
                type="text"
                value={editForm.whatsapp || ''}
                onChange={(e) => setEditForm({...editForm, whatsapp: e.target.value})}
                placeholder="מספר טלפון עם קידומת מדינה, לדוגמה: 972501234567"
                className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-green-50"
                dir="ltr"
              />
              <p className="text-xs text-gray-500 mt-1">הכנס מספר בפורמט בינלאומי (בלי + או מקפים)</p>
            </div>

            {/* Facebook */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </span>
              </label>
              <input
                type="text"
                value={editForm.facebook || ''}
                onChange={(e) => setEditForm({...editForm, facebook: e.target.value})}
                placeholder="קישור לפרופיל או שם משתמש, לדוגמה: https://facebook.com/yourname"
                className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50"
                dir="ltr"
              />
            </div>

            {/* Instagram */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  Instagram
                </span>
              </label>
              <input
                type="text"
                value={editForm.instagram || ''}
                onChange={(e) => setEditForm({...editForm, instagram: e.target.value})}
                placeholder="שם משתמש, לדוגמה: @yourname"
                className="w-full px-4 py-2 border border-pink-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-pink-50"
                dir="ltr"
              />
            </div>

            {/* YouTube */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  YouTube
                </span>
              </label>
              <input
                type="text"
                value={editForm.youtube || ''}
                onChange={(e) => setEditForm({...editForm, youtube: e.target.value})}
                placeholder="קישור לערוץ, לדוגמה: https://youtube.com/@yourname"
                className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-red-50"
                dir="ltr"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSaving ? 'שומר...' : '💾 שמור שינויים'}
              </Button>
              <Button
                onClick={() => setIsEditModalOpen(false)}
                variant="outline"
                disabled={isSaving}
              >
                ביטול
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ShareCardDialog
        open={isShareCardOpen}
        onClose={() => setIsShareCardOpen(false)}
        profile={profile}
        stats={computedStats}
      />
    </div>
  );
};

export default ProfilePage;
